import { type App, reactive, type Plugin, type ComponentCustomProperties } from 'vue'
import DialogAlert from './DialogAlert.vue'

// store
export type Color = 'info' | 'success' | 'warning' | 'red-lighten-1'
export interface DialogStore {
  dialog: boolean
  title: string
  msg: string
  color: Color
  callback: Callback | null
  cancelCallback: Callback | null
  // confirm의 확인 콜백이 Promise를 돌려주면 끝날 때까지 다이얼로그를 열어 두고 확인/취소를 잠근다.
  processing: boolean
  // 다이얼로그를 새로 띄울 때마다 증가. 처리 중 콜백이 결과 알림 등 새 다이얼로그를 띄웠는지 구분하는 데 쓴다.
  seq: number
}
const dialogStore = reactive<DialogStore>({
  dialog: false,
  title: '',
  msg: '',
  color: 'info',
  callback: null,
  cancelCallback: null,
  processing: false,
  seq: 0,
})

// call
// 새 다이얼로그는 이전 다이얼로그의 콜백을 이어받지 않는다. 취소로 닫힌 confirm 의 콜백이 남아
// 이후 success/info 등의 '확인' 클릭으로 실행되던 문제(메모 삭제 오동작) 방지.
// 콜백이 필요하면 다이얼로그를 띄운 "후에" $dialog.callback()/cancel() 로 등록해야 한다.
const call = (title: string, msg: string, color: Color) => {
  dialogStore.dialog = true
  dialogStore.title = title
  dialogStore.msg = msg
  dialogStore.color = color
  dialogStore.callback = null
  dialogStore.cancelCallback = null
  dialogStore.processing = false
  dialogStore.seq++
}

// close
const close = () => {
  dialogStore.dialog = false
  dialogStore.title = ''
  dialogStore.msg = ''
  dialogStore.color = 'info'
  dialogStore.callback = null
  dialogStore.cancelCallback = null
  dialogStore.processing = false
}

// set callback
// async 콜백(Promise 반환)도 받는다 — confirm에서는 끝날 때까지 "처리 중..."으로 대기한다.
type Callback = () => void | Promise<unknown>
const setCallback = (cb: Callback) => {
  dialogStore.callback = cb
}

interface Dialog {
  info: (title: string, msg: string) => void
  success: (title: string, msg: string) => void
  warn: (title: string, msg: string) => void
  error: (title: string, msg: string) => void
  confirm: (title: string, msg: string, onConfirm: Callback, onCancel?: Callback) => void
  close: () => void
  callback: (cb: Callback) => void
  cancel: (cb: Callback) => void
}

export const $dialog: Dialog = {
  info: (title: string, msg: string) => call(title, msg, 'info'),
  success: (title: string, msg: string) => call(title, msg, 'success'),
  warn: (title: string, msg: string) => call(title, msg, 'warning'),
  error: (title: string, msg: string) => call(title, msg, 'red-lighten-1'),
  confirm: (title: string, msg: string, onConfirm: Callback, onCancel?: Callback) => {
    call(title, msg, 'warning')
    dialogStore.callback = onConfirm
    dialogStore.cancelCallback = onCancel || (() => {})
  },
  close: () => close(),
  callback: (cb: Callback) => setCallback(cb),
  cancel: (cb: Callback) => { dialogStore.cancelCallback = cb },
}

const DialogAlertPlugin = {
  install(app: App) {
    app.config.globalProperties.$dialog = $dialog

    // Composition API를 위한 Provide
    app.provide('dialog', $dialog)
    app.provide('dialogStore', dialogStore)

    // 전역 컴포넌트 등록
    app.component('DialogAlert', DialogAlert)
  },
}

export default DialogAlertPlugin as Plugin

// --------------------------------------------------------------
// intellisense 를 위한 설정
// --------------------------------------------------------------
declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $dialog: Dialog
  }
}

// Composition API - provide/inject를 위한 전역 키 정의
declare module 'vue' {
  export function inject(key: 'dialog'): Dialog
  export function inject(key: 'dialogStore'): DialogStore
}
